import React, { useState, useEffect } from 'react';
import { Button, message, Modal } from 'antd';
import { LogoutOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { history } from 'umi';
import { useAppSelector } from '@/common/hooks/store';
import './index.scss';

const ImpersonationBar = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [isUserComplete, setIsUserComplete] = useState(false);
  const userInfo = useAppSelector(state => state.account?.userInfo);

  useEffect(() => {
    // Check if we're in impersonation mode
    const impersonating = localStorage.getItem('isImpersonating') === 'true';
    setIsImpersonating(impersonating);

    // Add/remove body class for top padding
    if (impersonating) {
      document.body.classList.add('impersonating');
    } else {
      document.body.classList.remove('impersonating');
    }

    // Check if user has completed diagnosis and legal forms (for display purposes)
    if (impersonating && userInfo) {
      const { has_diagnosis, has_legal } = userInfo;
      const isComplete = has_diagnosis === 1 && has_legal === 1;
      setIsUserComplete(isComplete);
    }

    // Cleanup function
    return () => {
      if (!impersonating) {
        document.body.classList.remove('impersonating');
      }
    };
  }, [userInfo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('impersonating');
    };
  }, []);

  const handleDisconnect = () => {
    // Show different confirmation message based on user completion status
    const confirmTitle = isUserComplete
      ? "Déconnexion de l'impersonation"
      : 'Déconnexion - Diagnostic incomplet';

    const confirmContent = isUserComplete ? (
      "Êtes-vous sûr de vouloir arrêter l'impersonation de cet utilisateur ?"
    ) : (
      <div>
        <p>Cet utilisateur n'a pas terminé son parcours :</p>
        <ul style={{ marginTop: 8, paddingLeft: 20 }}>
          {userInfo?.has_diagnosis !== 1 && <li>Diagnostic Optivest</li>}
          {userInfo?.has_legal !== 1 && <li>Formulaire légal</li>}
        </ul>
        <p style={{ marginTop: 12 }}>
          Voulez-vous quand même arrêter l'impersonation ? Vous serez redirigé
          vers le tableau de bord.
        </p>
      </div>
    );

    Modal.confirm({
      title: confirmTitle,
      content: confirmContent,
      icon: (
        <ExclamationCircleOutlined
          style={{ color: isUserComplete ? '#1890ff' : '#faad14' }}
        />
      ),
      okText: 'Oui, déconnecter',
      cancelText: 'Annuler',
      onOk: () => {
        try {
          // Clear impersonation state
          localStorage.removeItem('isImpersonating');
          localStorage.removeItem('originalAdminToken');
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');

          // Clear any other user-related data
          localStorage.removeItem('diagnosisFormData');
          localStorage.removeItem('leagalFormData');
          localStorage.removeItem('diagnosisFormDataIsEnd');
          localStorage.removeItem('leagalFormDataIsEnd');

          // Show different success messages based on completion status
          if (isUserComplete) {
            message.success('Impersonation terminée avec succès');
          } else {
            message.warning(
              'Impersonation terminée - Utilisateur avec diagnostic incomplet',
            );
          }

          // Always redirect to login (admin can choose where to go next)
          window.location.href = '/login';
        } catch (error) {
          console.error('Error disconnecting from impersonation:', error);
          message.error('Erreur lors de la déconnexion');
        }
      },
    });
  };

  // Don't render if not impersonating
  if (!isImpersonating) {
    return null;
  }

  return (
    <div className="impersonation-bar">
      <div className="impersonation-bar-content">
        <div className="impersonation-info">
          <span className="impersonation-icon">👤</span>
          <span className="impersonation-text">
            Mode impersonation - {userInfo?.email || 'Utilisateur inconnu'}
          </span>
        </div>

        <Button
          type="primary"
          danger
          icon={<LogoutOutlined />}
          onClick={handleDisconnect}
          className="disconnect-button"
          title="Arrêter l'impersonation"
        >
          {isUserComplete ? 'Déconnecter' : 'Déconnecter (incomplet)'}
        </Button>
      </div>
    </div>
  );
};

export default ImpersonationBar;
